import { useEffect, useRef, useState } from "react";
import { NeuralWaveform } from "./NeuralWaveform";
import WaveCallouts from "./WaveCallouts";

/**
 * Bare <canvas> running the NeuralWaveform engine.
 *
 * Sizes itself to its parent, so drop it inside an existing positioned,
 * overflow-hidden container (see `.dashboard-main-card` / `.wave-canvas` in
 * LandingPage's style block). Every per-frame value lives inside the engine
 * instance — nothing goes through React state, so this component renders once.
 *
 * `options` must be a stable reference (module-level constant or useMemo);
 * changing it tears the engine down and rebuilds it.
 *
 * `onEngine` is called with the live instance after start, and with null on
 * teardown, so siblings can sample the wave.
 */
export function WaveformCanvas({ className = "wave-canvas", options, onEngine }) {
  const canvasRef = useRef(null);
  const onEngineRef = useRef(onEngine);
  onEngineRef.current = onEngine;

  useEffect(() => {
    const canvas = canvasRef.current;
    // No 2D canvas (blocked, lost, or a test DOM): skip the decorative wave rather than crash the page.
    if (!canvas || !canvas.getContext("2d")) return undefined;
    const wave = new NeuralWaveform(canvas, options);
    wave.start();
    onEngineRef.current?.(wave);
    // Required: without it, Strict Mode's double-invoke in dev leaves two
    // engines painting over each other, and unmount leaks a rAF loop.
    return () => {
      onEngineRef.current?.(null);
      wave.destroy();
    };
  }, [options]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}

/**
 * The waveform plus the three callouts anchored to it. One state flip when the
 * engine comes up; after that both layers animate off the same rAF-driven
 * instance without re-rendering.
 */
export default function DashboardWaveform({ options }) {
  const [engine, setEngine] = useState(null);
  return (
    <>
      <WaveformCanvas options={options} onEngine={setEngine} />
      <WaveCallouts engine={engine} />
    </>
  );
}
